<template>
  <div class="container py-4 delivery-page">
    <div class="row">
      <div class="col-12 mb-3">
        <div class="delivery-hero">
          <small class="delivery-hero__eyebrow">Cobertura de entrega</small>
          <h2 class="delivery-hero__title">Revisa tu zona antes de pedir</h2>
          <p class="delivery-hero__text" v-if="hasKmZones">
            El valor de envío se calcula según distancia. Revisa el mapa y los
            tramos disponibles.
          </p>
          <p class="delivery-hero__text" v-else>
            Estas son las zonas de reparto configuradas actualmente para el
            local.
          </p>
        </div>
      </div>

      <div class="col-12 col-lg-4 mb-3">
        <div class="delivery-panel">
          <div class="delivery-panel__header">
            <small class="delivery-panel__label">Resumen</small>
            <h5 class="mb-0">Zonas disponibles</h5>
          </div>

          <div class="delivery-km-summary" v-if="hasKmZones">
            <div class="delivery-km-summary__item">
              <small>Tarifa base</small>
              <strong>Desde ${{ formatNumber(minKmValue) }}</strong>
            </div>
            <div class="delivery-km-summary__item" v-if="maxKmDistance">
              <small>Alcance estimado</small>
              <strong>Hasta {{ formatKm(maxKmDistance) }}</strong>
            </div>
          </div>

          <div class="delivery-zone-list custom_scroll">
            <button
              type="button"
              class="delivery-zone-card"
              v-for="(zone, i) in getZones"
              :key="i"
              :class="getZoneCardClass(zone)"
              :style="{ '--delivery-zone-color': zone.color || 'var(--delivery-accent)' }"
              :aria-pressed="activeZoneName === zone.name"
              @click="focusZone(zone)"
            >
              <div class="delivery-zone-card__top">
                <span class="delivery-zone-card__name">{{ zone.name }}</span>
                <span class="delivery-zone-card__price" v-if="zone.value">
                  ${{ formatNumber(zone.value) }}
                </span>
                <span class="delivery-zone-card__price" v-else-if="zone.type == 'km' && zone.kms && zone.kms.length">
                  Desde ${{ formatNumber(zone.kms[0].value) }}
                </span>
              </div>

              <small class="delivery-zone-card__meta" v-if="zone.type == 'km'">
                Tarifa por distancia
              </small>
              <small class="delivery-zone-card__meta" v-else>
                Zona fija de reparto
              </small>

              <div
                class="delivery-zone-card__ranges"
                v-if="zone.type == 'km' && zone.kms && zone.kms.length"
              >
                <div
                  class="delivery-range"
                  v-for="(km, index) in zone.kms"
                  :key="`${zone.name}-${index}`"
                >
                  <span>{{ formatKm(km.start) }} - {{ formatKm(km.end) }}</span>
                  <strong>${{ formatNumber(km.value) }}</strong>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-8">
        <div class="delivery-map-card">
          <div class="delivery-map-card__header">
            <div>
              <small class="delivery-panel__label">Mapa</small>
              <h5 class="mb-0">Cobertura actual</h5>
            </div>
            <div class="delivery-map-card__legend">
              <span class="legend-dot"></span>
              <small>Toca una zona para enfocarla</small>
            </div>
          </div>

          <div class="delivery-map-frame">
            <gmap-map
              :center="point"
              :zoom="zoom"
              style="width: 100%; min-height: 62vh"
            >
              <component
                :is="path.type == 'zone' ? 'gmap-polygon' : 'gmap-circle'"
                v-for="(path, i) in zones"
                :key="`path-${i}`"
                :paths="path.paths"
                :center="path.center || point"
                :radius="path.radius"
                :options="getMapOptions(path)"
              />
            </gmap-map>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCenter } from "geolib";

export default {
  data() {
    return {
      point: { lat: -36.8001887, lng: -73.004514 },
      zoom: 11.2,
      activeZoneName: "",
    };
  },
  mounted() {
    this.centerMap();
  },
  methods: {
    getCenterMarker(zone) {
      let zones = [];
      let z = zone.paths.map((z) => ({ latitude: z.lat, longitude: z.lng }));
      zones.push(...z);
      let center = getCenter(zones);
      return { lat: center.latitude, lng: center.longitude };
    },
    centerMap() {
      let zones = [];
      this.zones.forEach((zone) => {
        if (zone.type == "km" && zone.center) {
          zones.push({
            latitude: zone.center.lat,
            longitude: zone.center.lng,
          });
          return;
        }

        let z = (zone.paths || []).map((z) => ({
          latitude: z.lat,
          longitude: z.lng,
        }));
        zones.push(...z);
      });

      let center = getCenter(zones);

      if (center) this.point = { lat: center.latitude, lng: center.longitude };
      this.zoom = this.hasKmZones ? 11.4 : 10.9;
    },
    focusZone(zone) {
      if (!zone) return;
      this.activeZoneName = zone.name || "";

      if (zone.type == "km" && zone.center) {
        this.point = {
          lat: Number(zone.center.lat),
          lng: Number(zone.center.lng),
        };
        const radius = Number(zone.radius) || 0;
        if (radius >= 8000) this.zoom = 11.1;
        else if (radius >= 5000) this.zoom = 11.6;
        else if (radius >= 3000) this.zoom = 12.1;
        else this.zoom = 12.7;
        return;
      }

      if (zone.paths && zone.paths.length > 0) {
        const center = this.getCenterMarker(zone);
        if (center) {
          this.point = center;
          const lats = zone.paths.map((point) => Number(point.lat) || 0);
          const lngs = zone.paths.map((point) => Number(point.lng) || 0);
          const latSpan = Math.max(...lats) - Math.min(...lats);
          const lngSpan = Math.max(...lngs) - Math.min(...lngs);
          const span = Math.max(latSpan, lngSpan);

          if (span > 0.18) this.zoom = 11;
          else if (span > 0.12) this.zoom = 11.5;
          else if (span > 0.07) this.zoom = 12;
          else this.zoom = 12.6;
        }
      }
    },
    formatKm(value) {
      const distance = Number(value) || 0;
      if (distance >= 1000) return `${(distance / 1000).toFixed(1)} km`;
      return `${distance} m`;
    },
    getMapOptions(path) {
      const isActive = !this.activeZoneName || this.activeZoneName === path.name;
      return {
        strokeColor: path.color,
        strokeOpacity: isActive ? 0.95 : 0.28,
        strokeWeight: isActive ? (path.type == "km" ? 3 : 2.5) : 1.4,
        fillColor: path.color,
        fillOpacity: isActive
          ? (path.type == "km" ? 0.18 : 0.14)
          : 0.05,
      };
    },
    getZoneCardClass(zone) {
      return {
        "delivery-zone-card--km": zone.type == "km",
        "delivery-zone-card--active": this.activeZoneName === zone.name,
      };
    },
  },
  computed: {
    getZones() {
      return this.zones.filter((z) => z.component !== "banned");
    },
    zones() {
      let data = [];
      if (this.info && this.info.settings && this.info.settings.zones)
        data = this.info.settings.zones;
      return data;
    },
    hasKmZones() {
      return this.getZones.some((zone) => zone.type == "km");
    },
    minKmValue() {
      const values = this.getZones
        .filter((zone) => zone.type == "km" && zone.kms && zone.kms.length)
        .flatMap((zone) => zone.kms.map((km) => Number(km.value) || 0))
        .filter((value) => value > 0);

      if (!values.length) return 0;
      return Math.min(...values);
    },
    maxKmDistance() {
      const distances = this.getZones
        .filter((zone) => zone.type == "km" && zone.kms && zone.kms.length)
        .flatMap((zone) => zone.kms.map((km) => Number(km.end) || 0))
        .filter((value) => value > 0);

      if (!distances.length) return 0;
      return Math.max(...distances);
    },
    info() {
      return this.$store.state.info;
    },
  },
  watch: {
    info() {
      this.centerMap();
      this.activeZoneName = "";
    },
  },
};
</script>

<style scoped>
.delivery-page {
  --delivery-accent: var(--ticketapp-primary-color, #2b83c6);
  --delivery-action: var(--ticketapp-button-color, var(--delivery-accent));
  --delivery-action-text: var(--ticketapp-button-text-color, #ffffff);
  --delivery-ink: var(--ticketapp-page-text-color, #334155);
  --delivery-title: var(--ticketapp-title-color, #1e293b);
  --delivery-soft: color-mix(in srgb, var(--delivery-accent) 9%, white);
  --delivery-border: color-mix(in srgb, var(--delivery-accent) 22%, white);
  --delivery-muted: color-mix(in srgb, var(--delivery-ink) 70%, white);
}

.delivery-hero,
.delivery-panel,
.delivery-map-card {
  background: #ffffff;
  border: 1px solid var(--delivery-border);
  border-radius: 20px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.06);
}

.delivery-hero {
  padding: clamp(1.4rem, 3vw, 2.35rem);
  border-left: 5px solid var(--delivery-accent);
  background: linear-gradient(115deg, var(--delivery-soft), #ffffff 62%);
}

.delivery-hero__eyebrow,
.delivery-panel__label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--delivery-accent);
}

.delivery-hero__title {
  margin-bottom: 0.35rem;
  font-weight: 800;
  color: var(--delivery-title);
}

.delivery-hero__text {
  margin-bottom: 0;
  color: var(--delivery-muted);
}

.delivery-panel,
.delivery-map-card {
  padding: 1rem;
}

.delivery-panel__header {
  margin-bottom: 1rem;
  padding-bottom: 0.8rem;
  border-bottom: 1px solid var(--delivery-border);
}

.delivery-map-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.delivery-panel__header h5,
.delivery-map-card__header h5 {
  color: var(--delivery-title);
  font-weight: 750;
}

.delivery-zone-list {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.delivery-zone-card {
  width: 100%;
  margin-bottom: 0.8rem;
  padding: 1rem;
  border: 1px solid var(--delivery-border);
  border-left: 5px solid var(--delivery-zone-color);
  border-radius: 16px;
  background: #ffffff;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.delivery-zone-card:hover {
  transform: translateY(-1px);
  border-color: var(--delivery-accent);
  border-left-color: var(--delivery-zone-color);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
}

.delivery-zone-card--km {
  background: var(--delivery-soft);
}

.delivery-zone-card--active {
  border-color: var(--delivery-accent);
  border-left-color: var(--delivery-zone-color);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--delivery-accent) 16%, transparent);
}

.delivery-zone-card:focus-visible {
  outline: 3px solid var(--delivery-accent);
  outline-offset: 2px;
}

.delivery-zone-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.delivery-zone-card__name {
  font-weight: 800;
  text-transform: uppercase;
  color: var(--delivery-title);
}

.delivery-zone-card__price {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background: var(--delivery-action);
  color: var(--delivery-action-text);
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
}

.delivery-zone-card__meta {
  display: block;
  margin-top: 0.45rem;
  color: var(--delivery-muted);
}

.delivery-zone-card__ranges {
  display: grid;
  gap: 0.45rem;
  margin-top: 0.8rem;
}

.delivery-range {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0.75rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid var(--delivery-border);
  color: var(--delivery-ink);
  font-size: 0.92rem;
}

.delivery-km-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.delivery-km-summary__item {
  padding: 0.85rem 0.95rem;
  border-radius: 16px;
  background: var(--delivery-soft);
  border: 1px solid var(--delivery-border);
}

.delivery-km-summary__item small {
  display: block;
  color: var(--delivery-accent);
  margin-bottom: 0.2rem;
}

.delivery-km-summary__item strong {
  color: var(--delivery-title);
  font-size: 1rem;
}

.delivery-map-card__legend {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--delivery-muted);
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: var(--delivery-accent);
}

.delivery-map-frame {
  overflow: hidden;
  border: 1px solid var(--delivery-border);
  border-radius: 14px;
  background: var(--delivery-soft);
}

.bg-dark .delivery-hero,
.bg-dark .delivery-panel,
.bg-dark .delivery-map-card {
  background: rgba(15, 23, 42, 0.78);
  border-color: rgba(71, 85, 105, 0.9);
}

.bg-dark .delivery-hero__title,
.bg-dark .delivery-zone-card__name,
.bg-dark .delivery-range,
.bg-dark .delivery-map-card__header h5,
.bg-dark .delivery-panel__header h5 {
  color: #f8fafc;
}

.bg-dark .delivery-hero__text,
.bg-dark .delivery-zone-card__meta,
.bg-dark .delivery-map-card__legend {
  color: #cbd5e1;
}

.bg-dark .delivery-zone-card {
  border-color: rgba(71, 85, 105, 0.9);
  border-left-color: var(--delivery-zone-color);
  background: linear-gradient(180deg, rgba(30, 41, 59, 0.92) 0%, rgba(15, 23, 42, 0.96) 100%);
}

.bg-dark .delivery-zone-card--km {
  border-color: var(--delivery-accent);
  border-left-color: var(--delivery-zone-color);
  background: color-mix(in srgb, var(--delivery-accent) 16%, #0f172a);
}

.bg-dark .delivery-zone-card--active {
  border-color: var(--delivery-accent);
  border-left-color: var(--delivery-zone-color);
}

.bg-dark .delivery-range,
.bg-dark .delivery-km-summary__item {
  background: rgba(15, 23, 42, 0.72);
}

@media (max-width: 991px) {
  .delivery-map-card {
    margin-top: 0.25rem;
  }
}

@media (max-width: 767px) {
  .delivery-hero {
    padding: 1.25rem;
  }

  .delivery-hero__title {
    font-size: clamp(1.45rem, 6vw, 2rem);
  }

  .delivery-zone-list {
    max-height: none;
    overflow: visible;
  }

  .delivery-map-card__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
